import React, { useState, useEffect } from 'react';
import AdminSidebar from '../components/admin/AdminSidebar';
import LogoManagement from '../components/admin/LogoManagement';
import ContentManagement from '../components/admin/ContentManagement';
import ButtonLinksManagement from '../components/admin/ButtonLinksManagement';
import PDFDocumentManagement from '../components/admin/PDFDocumentManagement';
import SocialMediaManagement from '../components/admin/SocialMediaManagement';
import FileUploadManagement from '../components/admin/FileUploadManagement';
import FooterLinksManagement from '../components/admin/FooterLinksManagement';

const Admin = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('adminAuthenticated') === 'true';
  });
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });

  const handleLogin = (e) => {
    e.preventDefault();
    // Simple authentication - in production, use proper auth
    if (loginForm.username === 'admin' && loginForm.password === 'riverprime2024') {
      setIsAuthenticated(true);
      localStorage.setItem('adminAuthenticated', 'true');
    } else {
      alert('Invalid credentials');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuthenticated');
  };

  useEffect(() => {
    // Ensure admin body class is applied for specific admin styling
    document.body.classList.add('admin-body');
    return () => {
      document.body.classList.remove('admin-body');
    };
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="admin-login" style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2652a0 0%, #1a3d7a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div className="login-container" style={{ width: '100%', maxWidth: '400px' }}>
          <div className="login-card" style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '40px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="login-header" style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h1 style={{ color: '#ffdd00', marginBottom: '10px', fontSize: '2.5rem', fontWeight: '700' }}>RiverPrime Admin</h1>
              <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: '0', fontSize: '1.1rem' }}>Enter your credentials to access the admin panel</p>
            </div>
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#fff', marginBottom: '5px', fontWeight: '500' }}>Username</label>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                  placeholder="Enter username"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '16px'
                  }}
                />
              </div>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#fff', marginBottom: '5px', fontWeight: '500' }}>Password</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  placeholder="Enter password"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    fontSize: '16px'
                  }}
                />
              </div>
              <button type="submit" className="login-btn" style={{
                width: '100%',
                padding: '15px',
                background: 'linear-gradient(135deg, #ffdd00 0%, #ffc700 100%)',
                color: '#2652a0',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}>
                <i className="fas fa-sign-in-alt" style={{ marginRight: '8px' }}></i>
                Login to Admin Panel
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'logos':
        return <LogoManagement />;
      case 'content':
        return <ContentManagement />;
      case 'buttons':
        return <ButtonLinksManagement />;
      case 'documents':
        return <PDFDocumentManagement />;
      case 'social':
        return <SocialMediaManagement />;
      case 'uploads':
        return <FileUploadManagement />;
      case 'footer':
        return <FooterLinksManagement />;
      default:
        return (
          <div className="admin-dashboard">
            <div className="dashboard-header">
              <h1>RiverPrime Admin Dashboard</h1>
              <p>Welcome to the admin panel. Use the sidebar to manage different aspects of your website.</p>
            </div>
            <div className="dashboard-stats">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-image"></i>
                </div>
                <div className="stat-info">
                  <h3>Logo Management</h3>
                  <p>Manage logos and favicon</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-edit"></i>
                </div>
                <div className="stat-info">
                  <h3>Content Management</h3>
                  <p>Edit page content and metadata</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-link"></i>
                </div>
                <div className="stat-info">
                  <h3>Button Links</h3>
                  <p>Manage all button URLs</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-file-pdf"></i>
                </div>
                <div className="stat-info">
                  <h3>PDF Documents</h3>
                  <p>Upload and manage regulatory PDFs</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-share-alt"></i>
                </div>
                <div className="stat-info">
                  <h3>Social Media</h3>
                  <p>Update social links</p>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="admin-panel" style={{
      display: 'flex',
      minHeight: '100vh',
      background: '#f8f9fa',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    }}>
      <AdminSidebar 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
        onLogout={handleLogout}
      />
      <div className="admin-content" style={{
        flex: 1,
        marginLeft: '280px',
        padding: '20px',
        background: '#f8f9fa',
        minHeight: '100vh'
      }}>
        {renderActiveSection()}
      </div>
    </div>
  );
};

export default Admin;